/*
 * AIGO Facets — forked from facets.js
 * Renamed custom elements and DOM IDs to avoid conflicts with the Hyper theme.
 *
 * Element mapping:
 *   facet-short       → aigo-facet-short
 *   facet-form        → aigo-facet-form
 *   facet-remove      → aigo-facet-remove
 *   facet-count       → aigo-facet-count
 *   price-range       → aigo-price-range
 *   load-more-button  → aigo-load-more
 *
 * ID mapping:
 *   ProductGridContainer      → AigoProductGrid
 *   ProductsList              → AigoProductsList
 *   FacetFiltersContainer     → AigoFilters
 *   FacetFiltersActive        → AigoFiltersActive
 *   ProductCount              → AigoProductCount
 *   SortByContainer           → AigoSortBy
 */

class AigoFacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="aigo-facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="aigo-facet-form"]');
  }
}
customElements.define('aigo-facet-short', AigoFacetShort, { extends: 'select' });

class AigoPriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    if (!this.minPriceInput || !this.maxPriceInput || !this.minRangeInput || !this.maxRangeInput) return;

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('aigo-price-range', AigoPriceRange);

class AigoFacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('AigoProductGrid');
    const loadings = document.querySelectorAll('[data-aigo-facet-loading]');

    if (container) container.style.opacity = '0.4';

    setTimeout(() => {
      const target = document.querySelector('.shop-main');
      if (target) {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 200,
          behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
        });
      }
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('is-loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('AigoProductGrid');
    const loadings = document.querySelectorAll('[data-aigo-facet-loading]');

    if (container) container.style.opacity = '1';

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('is-loading');
      });
    }

    document.dispatchEvent(new CustomEvent('aigo:collection:rerendered'));

    if (container && window.FoxTheme && typeof window.FoxTheme.loadProxyImages === 'function') {
      window.FoxTheme.loadProxyImages(container);
    }
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);

            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      })
      .catch(() => {
        this.afterRenderSection();
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll('#AigoFilters [data-filter]');
    const facetElementsFromDom = document.querySelectorAll('#AigoFilters [data-filter]');

    Array.from(facetElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.style.display = 'none';
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.style.display = '';
        if (filter.classList.contains('fgroup')) {
          const body = filter.querySelector('.fgroup__body');
          const sourceBody = element.querySelector('.fgroup__body');
          if (body && sourceBody) body.innerHTML = sourceBody.innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'AigoFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const source = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = source ? source.innerHTML : '';
  }

  renderProductGridContainer(responseText) {
    const id = 'AigoProductGrid';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const source = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = source ? source.innerHTML : '';
  }

  renderProductCount(responseText) {
    const id = 'AigoProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const source = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = source ? source.innerHTML : '';
  }

  renderSortBy(responseText) {
    const id = 'AigoSortBy';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const source = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = source ? source.innerHTML : '';
  }
}
customElements.define('aigo-facet-form', AigoFacetForm, { extends: 'form' });

class AigoFacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="aigo-facet-form"]') || document.querySelector('form[is="aigo-facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('aigo-facet-remove', AigoFacetRemove, { extends: 'a' });

class AigoFacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('aigo-facet-count');
    this.innerText = facetCount ? facetCount.innerHTML : '';
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('aigo-facet-count', AigoFacetCount);

class AigoLoadMore extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('data-type') === 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('is-loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGrid(responseText);
      });
  }

  renderPagination(responseText) {
    const container = document.getElementById('AigoProductGrid');
    if (container === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = container.querySelector('.pager');
    const source = parsedHTML.querySelector('.pager');

    if (source && pagination) {
      pagination.innerHTML = source.innerHTML;
    } else if (!source && pagination) {
      pagination.remove();
    }
  }

  renderProductGrid(responseText) {
    const container = document.getElementById('AigoProductGrid');
    if (container === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productGrid = container.querySelector('.cgrid');
    const source = parsedHTML.querySelector('.cgrid');

    if (source && productGrid) {
      source.querySelectorAll('.cgrid > *').forEach((item) => {
        productGrid.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('is-loading');
    this.style.pointerEvents = 'none';
  }
}
customElements.define('aigo-load-more', AigoLoadMore, { extends: 'button' });
